import {
  adminDebtPaymentsApi,
  type AdminDebtRequest,
} from '@/api/admin/adminDebtPaymentsApi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import {
  currencyFormatter,
  formatMovementDate,
  formatTransferDate,
} from '@/utils/movements-utils';
import { getAxiosErrorStatus, handleGlobalError } from '@/utils/error-handler';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * The request is settled — or gone — and this dialog's copy of it is stale.
 *
 * 400 is what the backend answers when the row is no longer PENDING_REVIEW
 * (`InvalidAccountBalanceException`, mapped to BAD_REQUEST in
 * `CustomExceptionHandler`), 404 when it no longer exists, and 409 is kept for
 * the day that mapping moves to the status the conflict actually is.
 *
 * Reading 400 as "settled" holds only because the other 400 these two endpoints
 * can produce — rejecting with a blank reason — is already impossible from here:
 * the Rechazar button below stays disabled until a reason is typed. Send a
 * request this dialog has not validated and that invariant is gone.
 */
const SETTLED_STATUSES = [400, 404, 409];

interface Props {
  request: AdminDebtRequest | null;
  onClose: () => void;
  onReviewed: () => void;
}

export const DebtRequestDetailDialog = ({
  request,
  onClose,
  onReviewed,
}: Props) => {
  const [reviewNotes, setReviewNotes] = useState('');
  const [receipt, setReceipt] = useState<string | null>(null);
  const [receiptFailed, setReceiptFailed] = useState(false);
  const [runningAction, setRunningAction] = useState<
    'approve' | 'reject' | null
  >(null);

  useEffect(() => {
    setReviewNotes('');
    setReceipt(null);
    setReceiptFailed(false);
    if (!request) return;

    let active = true;
    // The DTO's own receiptUrl is the raw S3 URL and may not be reachable. The
    // signed one is fetched per open so it is still valid when clicked.
    adminDebtPaymentsApi
      .receiptUrl(request.id)
      .then((url) => {
        if (!active) return;
        // API-stored URLs are untrusted (security.md's "External URLs" rule):
        // only accept https, everything else falls through to the same
        // "couldn't load it" branch as a fetch failure.
        if (url.startsWith('https://')) {
          setReceipt(url);
        } else {
          setReceiptFailed(true);
        }
      })
      .catch(() => {
        // A missing receipt must not take the dialog down: the rest of the
        // request is still reviewable, and the admin needs to see why.
        if (active) setReceiptFailed(true);
      });
    return () => {
      active = false;
    };
  }, [request]);

  if (!request) return null;

  const isRunning = runningAction !== null;

  const review = async (action: 'approve' | 'reject') => {
    if (isRunning) return;
    setRunningAction(action);
    try {
      if (action === 'approve') {
        await adminDebtPaymentsApi.approve(
          request.id,
          reviewNotes.trim() || undefined,
        );
        toast({ title: 'Transferencia aprobada' });
      } else {
        await adminDebtPaymentsApi.reject(request.id, reviewNotes.trim());
        toast({ title: 'Transferencia rechazada' });
      }
      onReviewed();
      onClose();
    } catch (error) {
      const status = getAxiosErrorStatus(error);
      const alreadySettled = status !== undefined && SETTLED_STATUSES.includes(status);
      handleGlobalError(
        error,
        'No se pudo revisar la solicitud',
        alreadySettled
          ? 'Puede que otro admin la haya revisado primero.'
          : 'Revisá la conexión e intentá de nuevo.',
      );
      // Only close on the statuses that mean the request is no longer ours to
      // review. There the snapshot this dialog holds has gone stale — the list
      // reload never touches it, so staying open would keep offering
      // Aprobar/Rechazar for something that is not PENDING_REVIEW anymore — and
      // losing the typed notes is the right trade.
      //
      // Everything else leaves the request exactly where it was: a dropped
      // connection, a 500, or the 403 that ImpersonationReadOnlyFilter returns
      // during a support session without takeover. Closing on those would throw
      // away a review the admin still has to write, over an error that only
      // asks them to try again.
      if (alreadySettled) {
        onReviewed();
        onClose();
      }
    } finally {
      setRunningAction(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Revisar transferencia</DialogTitle>
          <DialogDescription>
            {request.user ? (
              <>
                <Link
                  to={`/admin/usuarios/${request.user.pk}`}
                  className="text-blue-600 hover:underline"
                >
                  {request.user.name || request.user.email}
                </Link>
                {request.user.name ? ` · ${request.user.email}` : ''}
              </>
            ) : (
              'Sin usuario'
            )}
          </DialogDescription>
        </DialogHeader>

        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Monto</dt>
            <dd className="font-medium">
              {currencyFormatter.format(request.amount)}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Fecha declarada</dt>
            <dd>{formatTransferDate(request.transferDate)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Comprobante</dt>
            <dd>
              {receipt ? (
                <a
                  href={receipt}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Ver comprobante
                </a>
              ) : receiptFailed ? (
                <span className="text-red-600">
                  No se pudo cargar el comprobante
                </span>
              ) : (
                <span className="text-gray-400">Cargando...</span>
              )}
            </dd>
          </div>
          {request.notes ? (
            <div>
              <dt className="text-gray-500">Notas de quien la envió</dt>
              <dd className="mt-1 text-gray-700">{request.notes}</dd>
            </div>
          ) : null}
          {request.status === 'APPROVED' || request.status === 'REJECTED' ? (
            <>
              <div className="flex justify-between">
                <dt className="text-gray-500">Revisado por</dt>
                <dd>
                  {request.reviewedBy?.name || request.reviewedBy?.email || '—'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Revisado el</dt>
                <dd>{formatMovementDate(request.reviewedAt)}</dd>
              </div>
            </>
          ) : null}
        </dl>

        {request.status === 'PENDING_REVIEW' ? (
          <>
            <Textarea
              value={reviewNotes}
              onChange={(event) => setReviewNotes(event.target.value)}
              placeholder="Motivo (obligatorio para rechazar)"
              rows={3}
            />
            <DialogFooter>
              <Button
                variant="outline"
                // The API refuses an empty reason too (400, "El motivo del
                // rechazo es obligatorio"), but stopping it here is what keeps a
                // rejection with no reason from ever being one — and what lets
                // SETTLED_STATUSES above read a 400 as "already reviewed".
                disabled={isRunning || !reviewNotes.trim()}
                onClick={() => review('reject')}
              >
                {runningAction === 'reject' ? 'Guardando...' : 'Rechazar'}
              </Button>
              <Button disabled={isRunning} onClick={() => review('approve')}>
                {runningAction === 'approve' ? 'Guardando...' : 'Aprobar'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <p className="text-sm text-gray-500">
            {request.status === 'CANCELLED'
              ? 'Quien la envió la canceló antes de que se revisara.'
              : 'Esta solicitud ya fue revisada.'}
            {request.reviewNotes ? ` Motivo: ${request.reviewNotes}` : ''}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
